import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  ListGroup,
  Spinner,
  Alert,
  Card,
  CardBody,
  CardHeader,
} from "react-bootstrap";
import Plyr from "plyr-react";
import "plyr-react/plyr.css";
import DOMPurify from "dompurify";
import { FaArrowRight, FaChevronRight } from "react-icons/fa";

const DemoClass = () => {
  const [liveClasses, setLiveClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const plyrRef = useRef();

  // Fetch live classes
  const fetchLiveClasses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/demo-class-list`
      );
      if (res.data.status === "1" && res.data.data) {
        setLiveClasses(res.data.data);
        if (res.data.data.length > 0) setSelectedClass(res.data.data[0]);
      } else {
        setError("No demo classes available.");
      }
    } catch (err) {
      setError("Failed to load demo classes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveClasses();
  }, [fetchLiveClasses]);

  // Update Plyr source when selectedClass changes
  useEffect(() => {
    if (selectedClass && plyrRef.current) {
      const player = plyrRef.current.plyr;
      player.source = {
        type: "video",
        sources: [{ src: selectedClass.video_url, provider: "youtube" }],
      };
    }
  }, [selectedClass]);

  return (
    <section className="live-class-section py-5">
      <div className="page_header"></div>
      <Container>
        <div className="page_header mb-md-4 text-center">
          <h2>Free Demo Classes</h2>
        </div>

        {loading && (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && liveClasses.length === 0 && (
          <div className="text-center p-4">No demo classes available.</div>
        )}

        {!loading && !error && liveClasses.length > 0 && (
          <Row className="gy-2">
            {/* Video List */}
            <Col md={12} lg={5} xl={4}>
              <Card>
                <CardHeader className="bg_light fw-semibold">
                  Video List
                </CardHeader>
                <CardBody>
                  <ListGroup className="demo_list">
                    {liveClasses.map((cls, index) => (
                      <ListGroup.Item
                        key={cls.id}
                        active={selectedClass?.id === cls.id}
                        action
                        onClick={() => setSelectedClass(cls)}
                        style={{ cursor: "pointer" }}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex">
                          <span className="me-2">{index + 1}.</span>
                          {cls.title}
                        </div>
                        <FaChevronRight />
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>

            {/* Video Player */}
            <Col md={12} lg={7} xl={8}>
              <Card>
                {selectedClass && (
                  <>
                    <CardHeader className="bg_light fw-semibold">
                      {selectedClass.title}
                    </CardHeader>
                    <CardBody>
                      <>
                        <Plyr
                          ref={plyrRef}
                          source={{
                            type: "video",
                            sources: [
                              {
                                src: selectedClass.video_url,
                                provider: "youtube",
                              },
                            ],
                          }}
                        />
                        {selectedClass.description && (
                          <div
                            className="mt-3"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                selectedClass.description
                              ),
                            }}
                          />
                        )}
                      </>
                    </CardBody>
                  </>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </section>
  );
};

export default DemoClass;
